/* eslint-disable no-console */
import axios, { AxiosError } from 'axios'
import axiosRetry, { exponentialDelay, isNetworkError, isRetryableError } from 'axios-retry'
import trim from 'lodash/trim'

const STATE_PENDING = 'pending'
const STATE_REJECTED = 'rejected'
const STATE_FULLFILLED = 'fullfilled'

class MultipartUploader extends EventTarget {
  constructor(filename, config = {}) {
    super()

    const {
      disk = Spark.getDisk(),
      abortBeforeunload = true,
      chuckSize = MultipartUploader.DEFAULT_FILE_CHUNK_SIZE,
      maxConcurrentParts = 3,
      contentType,
      retry,
    } = config

    this._totalSize = null

    this.filename = filename
    this.disk = disk
    this.abortBeforeunload = abortBeforeunload
    this.chuckSize = chuckSize
    this.maxConcurrentParts = maxConcurrentParts
    this.contentType = contentType
    this.retry = {
      retries: 10,
      retryDelay: exponentialDelay,
      retryCondition: this.retryCondition.bind(this),
      ...retry,
    }

    this.key = null
    this.uploadId = null

    this.uploadParts = []

    this.isStarted = false
    this.isCompletedOrAborted = false

    this.createMultipartHttp()
    this.createUploadHttp()

    this.initPromise = this.init().catch((e) => {
      console.error(e)
    })
  }

  retryCondition(err) {
    if (
      err.isAxiosError
      && (err.code === AxiosError.ERR_NETWORK || err.code === AxiosError.ERR_BAD_RESPONSE)
    ) {
      return true
    }

    return isNetworkError(err) || isRetryableError(err)
  }

  createMultipartHttp() {
    this.multipartHttp = axios.create()
    axiosRetry(this.multipartHttp, this.retry)
  }

  createUploadHttp() {
    this.uploadHttp = axios.create()
    this.uploadHttp.defaults.headers.common = {}
    axiosRetry(this.uploadHttp, this.retry)
  }

  /**
   * @returns {[Promise<void>]}
   */
  get uploadPromises() {
    return this.uploadParts.map(p => p.upload)
  }

  /**
   * @returns {[AbortController]}
   */
  get uploadControllers() {
    return this.uploadParts.map(p => p.controller)
  }

  /**
   * @returns {number}
   */
  get uploadedSize() {
    return this.uploadParts.reduce((previousValue, part) => {
      return previousValue + part.uploadedSize
    }, 0)
  }

  /**
   * @returns {[{ETag: string, PartNumber: number}]}
   */
  get uploadedPartResults() {
    return this.uploadParts.map(p => p.result)
  }

  /**
   * @returns {number}
   */
  get totalSize() {
    if (this._totalSize) {
      return this._totalSize
    }

    return this.uploadParts.reduce((previousValue, part) => {
      return previousValue + part.data.size
    }, 0)
  }

  /**
   * @returns {number}
   */
  get progress() {
    return Math.floor(this.uploadedSize / this.totalSize * 100)
  }

  initEvents() {
    if (this.abortBeforeunload) {
      window.addEventListener('beforeunload', () => {
        // abort multipart upload, when uploading and close browser
        if (this.isStarted && !this.isCompletedOrAborted) {
          const blob = new Blob(
            [JSON.stringify({
              key: this.key,
            })],
            {
              type: 'application/json',
            },
          )
          navigator.sendBeacon(
            Spark.router('api.upload.multipart.abort', { upload_id: this.uploadId }),
            blob,
          )
        }
      })
    }
  }

  dispatchProgressEvent() {
    this.dispatchEvent(
      new CustomEvent('progress', { detail: { progress: this.progress } }),
    )
  }

  /**
   * @returns {Promise<void>}
   */
  async init() {
    const res = await this.multipartHttp.post(
      Spark.router('api.upload.multipart.create'),
      {
        disk: this.disk,
        filename: this.filename,
        content_type: this.contentType,
      },
    )

    const data = res.data

    this.uploadId = data.upload_id
    this.key = data.key

    this.isStarted = true

    this.initEvents()
  }

  /**
   * @param {File} file
   * @returns {Promise<void>}
   */
  async upload(file) {
    this._totalSize = file.size
    const numChunks = Math.floor(this._totalSize / this.chuckSize) + 1

    for (let index = 1; index <= numChunks; index++) {
      const start = (index - 1) * this.chuckSize
      const end = index * this.chuckSize

      const blob = (index < numChunks) ? file.slice(start, end) : file.slice(start)
      await this.pendingUploadParts()
      await this.uploadPart(blob)
    }
  }

  async pendingUploadParts() {
    if (this.maxConcurrentParts <= 0) {
      return
    }

    const pendingUploads = this.uploadParts
      .filter(p => p.state === STATE_PENDING)
      .map(p => p.upload)

    if (pendingUploads.length >= this.maxConcurrentParts) {
      await Promise.race(pendingUploads)
    }
  }

  /**
   * @param {Blob} data
   * @returns {Promise<void>}
   */
  async uploadPart(data) {
    await this.initPromise

    console.log('Uploading part bytes:', data.size)

    const partNumber = this.uploadParts.length + 1

    // the uploading parts
    this.uploadParts.push({
      uploadedSize: 0,
      controller: new AbortController(),
      state: STATE_PENDING,
      partNumber,
      data,
    })

    this.uploadParts[partNumber - 1].upload = this.uploadPartToS3(partNumber)
  }

  /**
   * @param {number} partNumber
   * @returns {Promise<void>}
   */
  async uploadPartToS3(partNumber) {
    const part = this.uploadParts[partNumber - 1]

    try {
      const presignRes = await this.multipartHttp.post(
        Spark.router('api.upload.multipart.presign', { upload_id: this.uploadId }),
        {
          disk: this.disk,
          key: this.key,
          part_number: partNumber,
        },
        {
          signal: part.controller.signal,
        },
      )

      const uploadRes = await this.uploadHttp.put(
        presignRes.data.url,
        part.data,
        {
          signal: part.controller.signal,
        },
      )

      part.result = {
        ETag: trim(uploadRes.headers.etag, '"'),
        PartNumber: partNumber,
      }

      part.state = STATE_FULLFILLED

      part.uploadedSize = part.data.size
      this.dispatchProgressEvent()
    }
    catch (e) {
      part.state = STATE_REJECTED
      await Promise.reject(e)
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async retryRejectedUploads() {
    await Promise.allSettled(this.uploadPromises)

    const rejectedPartNumbers = this.uploadParts
      .filter(p => p.state === STATE_REJECTED)
      .map(p => p.partNumber)

    for (const partNumber of rejectedPartNumbers) {
      console.log('Retrying upload part number:', partNumber)
      await this.pendingUploadParts()

      const part = this.uploadParts[partNumber - 1]

      part.state = STATE_PENDING
      part.upload = this.uploadPartToS3(partNumber)
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async complete(retry = false) {
    if (retry) {
      await this.retryRejectedUploads()
    }

    await Promise.all(this.uploadPromises)

    await this.multipartHttp.post(
      Spark.router('api.upload.multipart.complete', { upload_id: this.uploadId }),
      {
        disk: this.disk,
        key: this.key,
        parts: this.uploadedPartResults,
      },
    )

    this.isCompletedOrAborted = true
  }

  /**
   * @returns {Promise<void>}
   */
  async abort() {
    if (this.isStarted) {
      this.uploadControllers.forEach((controller) => {
        controller.abort()
      })

      await this.multipartHttp.post(
        Spark.router('api.upload.multipart.abort', { upload_id: this.uploadId }),
        {
          disk: this.disk,
          key: this.key,
        },
      )

      this.isCompletedOrAborted = true
    }
  }
}

// multipart upload minimum part size is 5MiB
// https://docs.aws.amazon.com/AmazonS3/latest/userguide/qfacts.html
MultipartUploader.DEFAULT_FILE_CHUNK_SIZE = 5242880

export default MultipartUploader
